import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft,
  Users,
  Brain,
  Target,
  Star,
  Calendar,
  Clock,
  Settings,
  FileText,
  CheckCircle,
  XCircle,
  BarChart,
  Briefcase,
  MapPin
} from 'lucide-react';
import { format } from 'date-fns';

export default function ProjectDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);

  useEffect(() => {
    if (user && id) {
      fetchProjectDetails();
    }
  }, [user, id]);

  const fetchProjectDetails = async () => {
    try {
      // Fetch project details with related data
      const { data: projectData, error: projectError } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          profiles (
            id,
            full_name,
            company_name
          ),
          applications (
            id,
            status,
            application_date,
            profiles (
              id,
              full_name,
              title,
              location,
              avatar_url
            )
          ),
          interview_stage_templates (
            id,
            name,
            description,
            duration,
            order_index
          ),
          job_matches (
            id,
            profile_id,
            overall_score,
            skills_score,
            experience_score,
            location_score,
            education_score,
            match_details,
            profiles (
              id,
              full_name,
              title,
              location,
              avatar_url
            )
          )
        `)
        .eq('id', id)
        .single();

      if (projectError) throw projectError;

      setProject(projectData);
      setMatches(projectData.job_matches || []);
      setApplications(projectData.applications || []);

    } catch (error) {
      console.error('Error fetching project details:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load project details'
      });
      navigate('/admin/projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The requested project could not be found.
        </p>
        <Button onClick={() => navigate('/admin/projects')}>
          Return to Projects
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/projects')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{project.title}</h1>
            <p className="text-muted-foreground">
              {project.profiles?.company_name} • {project.department}
            </p>
          </div>
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>

        {/* Project Overview */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Status</h3>
                <div className="flex items-center gap-2">
                  <Badge variant={project.is_active ? 'default' : 'secondary'}>
                    {project.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <span className="text-sm">
                    Created {format(new Date(project.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Location</h3>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{project.location}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Applications</h3>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{applications.length} total applicants</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">AI Matches</h3>
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 text-muted-foreground" />
                  <span>{matches.length} potential matches</span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium mb-2">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {project.skills?.map((skill: string, index: number) => (
                  <Badge key={index} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Tabs defaultValue="matches">
          <TabsList>
            <TabsTrigger value="matches" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              AI Matches
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Pipeline
            </TabsTrigger>
            <TabsTrigger value="metrics" className="flex items-center gap-2">
              <BarChart className="h-4 w-4" />
              Metrics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="matches" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>AI-Matched Candidates</CardTitle>
                <CardDescription>
                  Potential candidates matched by our AI based on skills and experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {matches
                    .sort((a, b) => b.overall_score - a.overall_score)
                    .map((match) => (
                      <div 
                        key={match.id} 
                        className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/talent/${match.profiles.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <h4 className="font-medium">{match.profiles.full_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {match.profiles.title}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-2 mb-1">
                              <Star className="h-4 w-4 text-primary" />
                              <span className="font-medium">{match.overall_score}% Match</span>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant="secondary">
                                Skills: {match.skills_score}%
                              </Badge>
                              <Badge variant="secondary">
                                Experience: {match.experience_score}%
                              </Badge>
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            View Profile
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Applications</CardTitle>
                <CardDescription>
                  Manage and track candidate applications
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {applications.map((application) => (
                    <div 
                      key={application.id} 
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/talent/${application.profiles.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <h4 className="font-medium">{application.profiles.full_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {application.profiles.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-sm">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>Applied {format(new Date(application.application_date), 'MMM d, yyyy')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <Badge variant={
                          application.status === 'hired' ? 'default' :
                          application.status === 'rejected' ? 'destructive' :
                          'secondary'
                        }>
                          {application.status}
                        </Badge>
                        <Button variant="outline" size="sm">
                          View Profile
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pipeline" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Interview Pipeline</CardTitle>
                <CardDescription>
                  Track candidates through the interview process
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {project.interview_stage_templates?.map((stage: any) => (
                    <div key={stage.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{stage.name}</h3>
                        <Badge variant="outline">{stage.duration}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {stage.description}
                      </p>
                      <div className="mt-4">
                        {applications
                          .filter(app => app.status === stage.name.toLowerCase().replace(/\s+/g, '_'))
                          .map(app => (
                            <div 
                              key={app.id}
                              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 mb-2"
                            >
                              <div>
                                <p className="font-medium">{app.profiles.full_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {app.profiles.title}
                                </p>
                              </div>
                              <Button variant="outline" size="sm">
                                View Details
                              </Button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="metrics" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Application Metrics</CardTitle>
                  <CardDescription>
                    Key metrics and performance indicators
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-muted-foreground">Total Applications</span>
                        <span className="font-medium">{applications.length}</span>
                      </div>
                      <Progress 
                        value={applications.length > 0 ? 100 : 0} 
                        className="h-2" 
                      />
                    </div>

                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-muted-foreground">Match Quality</span>
                        <span className="font-medium">
                          {matches.length > 0 
                            ? `${Math.round(matches.reduce((sum, m) => sum + m.overall_score, 0) / matches.length)}%`
                            : 'N/A'
                          }
                        </span>
                      </div>
                      <Progress 
                        value={
                          matches.length > 0 
                            ? Math.round(matches.reduce((sum, m) => sum + m.overall_score, 0) / matches.length)
                            : 0
                        } 
                        className="h-2" 
                      />
                    </div>

                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-muted-foreground">Pipeline Progress</span>
                        <span className="font-medium">
                          {applications.length > 0
                            ? `${Math.round(applications.filter(a => a.status === 'hired').length / applications.length * 100)}%`
                            : 'N/A'
                          }
                        </span>
                      </div>
                      <Progress 
                        value={
                          applications.length > 0
                            ? Math.round(applications.filter(a => a.status === 'hired').length / applications.length * 100)
                            : 0
                        } 
                        className="h-2" 
                      />
                    </div>
                  
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Application Status</CardTitle>
                  <CardDescription>
                    Current status of all applications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['new', 'screening', 'interviewing', 'offer', 'hired', 'rejected'].map(status => {
                      const count = applications.filter(a => a.status === status).length;
                      const percentage = applications.length > 0 
                        ? Math.round((count / applications.length) * 100)
                        : 0;

                      return (
                        <div key={status}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-muted-foreground capitalize">
                              {status}
                            </span>
                            <span className="font-medium">{count}</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}